import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { useYupValidationResolver } from "../../../../hooks/useYupValidationResolver";
import { useTranslate } from "../../../../hooks/useTranslate";
import { isVersionUK } from "../../../../services/versionService";
import { addOneDay, getDateTime } from "../../../../utils/dateTimeHelper";

const INIT_FORMAT = "yyyy-MM-dd";

const validationSchema = yup.object({
    previousInstitutionName: yup.string().required("formErrors.required"),
    addressLine1: yup.string().required("formErrors.required"),
    addressLine2: yup.string(),
    city: yup.string().required("formErrors.required"),
    code: yup.string().required("formErrors.required"),
    area: yup.string().required("formErrors.required"),
    country: yup.string().required("formErrors.required"),
    previousInstitutionFrom: yup.string().required("formErrors.required"),
    previousInstitutionTo: yup
        .string()
        .required("formErrors.required")
        .test(
            "is-at-least-1-day-after-from",
            "previousEducation.form.leaveDateAfterError",
            function (value) {
                const { previousInstitutionFrom } = this.parent;
                if (!previousInstitutionFrom || !value) return true;

                const fromDT = getDateTime(previousInstitutionFrom, INIT_FORMAT);
                const toDT = getDateTime(value, INIT_FORMAT);
                if (!fromDT?.isValid || !toDT?.isValid) return true;

                const minAllowedTo = fromDT.plus({ days: 1 }).startOf("day");
                return toDT.toMillis() >= minAllowedTo.toMillis();
            },
        ),
    ...(isVersionUK ? { reasonForJoining: yup.string().required("formErrors.required") } : {}),
});

const emptyFields = {
    previousInstitutionName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    code: "",
    area: "",
    country: "",
    previousInstitutionFrom: "",
    previousInstitutionTo: "",
    ...(isVersionUK ? { reasonForJoining: "" } : {}),
};

export const useSchoolAddressForm = (data) => {
    const defaultValues = useMemo(() => {
        const dataWithoutNull = Object.fromEntries(Object.entries(data || {})
            .filter(([key]) => key in emptyFields)
            .filter(([, value]) => value !== null));

        return {
            ...emptyFields,
            ...dataWithoutNull,
        };
    }, [data]);

    const resolver = useYupValidationResolver(validationSchema);
    const { t } = useTranslate();

    const {
        control,
        handleSubmit,
        register,
        reset,
        formState: { errors: errorsKeys, isSubmitted },
        watch,
        setValue,
    } = useForm({
        resolver,
        defaultValues,
        mode: "onChange",
    });

    const previousInstitutionFrom = watch("previousInstitutionFrom");
    const previousInstitutionTo = watch("previousInstitutionTo");

    const minToDate = previousInstitutionFrom ? addOneDay(previousInstitutionFrom) : undefined;

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues]);

    const errors = Object.fromEntries(Object
        .entries(errorsKeys || {})
        .map(([key, { message, ...value }]) => [key, { ...value, message: t(message) }]));

    return {
        control,
        register,
        handleSubmit,
        reset,
        errors,
        isSubmitted,
        previousInstitutionFrom,
    };
};
